import { FC, useMemo, useState } from 'react';
import { Plus, X } from '@phosphor-icons/react';
import { IBlockBase } from '../../../endpoints/coachSessions';
import styles from './ComposableFields.module.scss';

/**
 * Bug 6a204e7a — Form esercitazione "componibile": niente più muro di campi
 * sempre aperti. I parametri sono CHIP attivabili: il coach mostra solo i campi
 * che usa, ne aggiunge altri con "+ campo", li rimuove se non servono. Tutti i
 * valori sono liberi. Quando campo (L×W) + n. giocatori sono noti, la densità
 * (m²/giocatore) è calcolata automaticamente.
 *
 * La "Mappa Campi × Tipo Blocco" (quali partono attivi) è un default sensato,
 * non una gabbia: ogni campo resta attivabile/rimovibile a piacere.
 */

type FieldKey =
  | 'durationMinutes'
  | 'ewt'
  | 'series'
  | 'reps'
  | 'load'
  | 'restSeconds'
  | 'fieldLengthM'
  | 'fieldWidthM'
  | 'notes'
  | 'staffNotes';

interface FieldDef {
  key: FieldKey;
  label: string;
  kind: 'number' | 'text' | 'textarea';
  suffix?: string;
  placeholder?: string;
  wide?: boolean;
}

const FIELDS: FieldDef[] = [
  { key: 'durationMinutes', label: 'Durata', kind: 'number', suffix: "'" },
  { key: 'ewt', label: 'EWT', kind: 'number', suffix: "'" },
  { key: 'series', label: 'Serie', kind: 'number' },
  { key: 'reps', label: 'Rip./Durata', kind: 'text', placeholder: "6 o 2×15'" },
  { key: 'load', label: 'Carico', kind: 'text', placeholder: '70% 1RM' },
  { key: 'restSeconds', label: 'Recupero', kind: 'number', suffix: 's' },
  { key: 'fieldLengthM', label: 'Campo L', kind: 'number', suffix: 'm' },
  { key: 'fieldWidthM', label: 'Campo W', kind: 'number', suffix: 'm' },
  { key: 'notes', label: 'Note', kind: 'textarea', wide: true, placeholder: 'Note visibili al team…' },
  { key: 'staffNotes', label: 'Note staff', kind: 'textarea', wide: true, placeholder: 'Solo staff…' },
];

const FIELD_BY_KEY: Record<FieldKey, FieldDef> = FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.key]: f }),
  {} as Record<FieldKey, FieldDef>,
);

/** Mappa Campi × Tipo Blocco — campi attivi di partenza (discrezionale, modificabile). */
const DEFAULT_ACTIVE: Record<string, FieldKey[]> = {
  sequential: ['durationMinutes', 'series', 'reps', 'restSeconds'],
  parallel: ['durationMinutes', 'fieldLengthM', 'fieldWidthM'],
  rotation: ['durationMinutes'],
};

interface ComposableFieldsProps {
  block: IBlockBase;
  blockType: string;
  /** Numero di giocatori del gruppo (per la densità m²/giocatore). */
  playerCount?: number;
  onChange: (patch: Partial<IBlockBase>) => void;
}

const hasValue = (v: unknown) => v !== undefined && v !== null && v !== '';

export const ComposableFields: FC<ComposableFieldsProps> = ({
  block,
  blockType,
  playerCount,
  onChange,
}) => {
  // Attivi = campi con valore ∪ default del tipo ∪ aggiunti a mano (− rimossi a mano)
  const initialActive = useMemo(() => {
    const set = new Set<FieldKey>(DEFAULT_ACTIVE[blockType] ?? ['durationMinutes']);
    for (const f of FIELDS) if (hasValue((block as any)[f.key])) set.add(f.key);
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [active, setActive] = useState<Set<FieldKey>>(initialActive);

  const activeFields = FIELDS.filter((f) => active.has(f.key));
  const inactiveFields = FIELDS.filter((f) => !active.has(f.key));

  const addField = (key: FieldKey) =>
    setActive((prev) => new Set(prev).add(key));

  const removeField = (key: FieldKey) => {
    setActive((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    onChange({ [key]: undefined } as Partial<IBlockBase>);
  };

  const setValue = (f: FieldDef, raw: string) => {
    const v = f.kind === 'number' ? (raw === '' ? undefined : Number(raw)) : raw;
    onChange({ [f.key]: v } as Partial<IBlockBase>);
  };

  const density = useMemo(() => {
    const l = block.fieldLengthM;
    const w = block.fieldWidthM;
    if (l && w && playerCount && playerCount > 0) {
      return Math.round(((l * w) / playerCount) * 10) / 10;
    }
    return null;
  }, [block.fieldLengthM, block.fieldWidthM, playerCount]);

  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className={styles.wrap}>
      <div className={styles.chips}>
        {activeFields.map((f) => (
          <div
            key={f.key}
            className={`${styles.chip} ${f.wide ? styles.chipWide : ''}`}
          >
            <span className={styles.chipLabel}>{f.label}</span>
            {f.kind === 'textarea' ? (
              <textarea
                className={styles.chipTextarea}
                value={(block as any)[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => setValue(f, e.target.value)}
              />
            ) : (
              <input
                className={styles.chipInput}
                type={f.kind === 'number' ? 'number' : 'text'}
                value={(block as any)[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => setValue(f, e.target.value)}
              />
            )}
            {f.suffix && <span className={styles.chipSuffix}>{f.suffix}</span>}
            <button
              type="button"
              className={styles.chipRemove}
              title="Rimuovi campo"
              onClick={() => removeField(f.key)}
            >
              <X size={11} />
            </button>
          </div>
        ))}

        {density != null && (
          <div className={styles.densityChip} title="Densità calcolata: area campo / giocatori">
            {density} m²/gioc
          </div>
        )}

        {inactiveFields.length > 0 && (
          <div className={styles.addWrap}>
            <button
              type="button"
              className={styles.addBtn}
              onClick={() => setAddOpen((o) => !o)}
            >
              <Plus size={12} weight="bold" /> campo
            </button>
            {addOpen && (
              <div className={styles.addMenu}>
                {inactiveFields.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={styles.addMenuItem}
                    onClick={() => {
                      addField(f.key);
                      setAddOpen(false);
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { FIELD_BY_KEY };
